import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { IF } from "../../../../../../components/hoc/If";
import { useCurrentOrganisationId } from "../../../../../../services/local/organisationContext/OrganisationContextModel";
import { useFlightPathUser } from "../../../../../auth_insight";
import { AppGridToolbarType } from "../../types/AppGrid_types";
import { AppGridToolbar } from "../base/AppGridToolbar_view";
import { AppGrid } from "../base/AppGrid_view";
import { ModalGridToolbar } from "../base/ModalGridToolbar_view";
import { PositionedSpinner } from "@flightpath/coreui/dist/ui/PositionedSpinner";
import { StakeholdersGridModel } from "./StakeholdersGridView_model";

interface StakeholdersGridProps {
  gridToolbarType?: AppGridToolbarType;
  onAssignSuccess?: (selectedItems: number[]) => void;
  selectedLinkItems?: number[];
  data: FP.Entities.IStakeholderSummary[];
  backendColumnDefs?: FP.Entities.IColumnDef[];
  connectedUsers?: FP.Entities.IUser[];
  onCellStateChange?: (
    organisationId: number,
    projectId: number
  ) => (cell: string, isEditMode?: boolean) => Promise<void>;
  preSelectedItemIds?: number[];
  refreshDataHandler: any;
  isLoading: boolean;
  orgUsers?: FP.Entities.IUser[];
  orgBusinessAreas?: FP.Entities.IBusinessArea[];
  orgRoles?: FP.Entities.IRole[];
}

export const StakeholdersGrid: React.FunctionComponent<StakeholdersGridProps> = observer(
  ({
    gridToolbarType = "base",
    onAssignSuccess,
    data,
    connectedUsers,
    onCellStateChange,
    backendColumnDefs,
    preSelectedItemIds,
    refreshDataHandler,
    isLoading,
    orgUsers,
    orgBusinessAreas,
    orgRoles
  }) => {
    const organisationId = useCurrentOrganisationId();
    const authUser = useFlightPathUser();
    const [searchParams] = useSearchParams();

    const gridRef = useRef<AppGrid>();
    const { projectId } = useParams<{ projectId: string }>();

    const [gridModel, setGridModel] = useState(
      () =>
        new StakeholdersGridModel(
          organisationId,
          +projectId,
          authUser,
          backendColumnDefs,
          searchParams,
          gridToolbarType,
          refreshDataHandler,
          orgUsers,
          orgBusinessAreas,
          orgRoles,
          data
        )
    );

    useEffect(() => {
      if (gridModel.projectId !== +projectId || gridModel.organisationId !== organisationId) {
        setGridModel(
          new StakeholdersGridModel(
            organisationId,
            +projectId,
            authUser,
            backendColumnDefs,
            searchParams,
            gridToolbarType,
            refreshDataHandler,
            orgUsers,
            orgBusinessAreas,
            orgRoles,
            data
          )
        );
      }
      // eslint-disable-next-line
    }, [organisationId, projectId]);

    useEffect(() => {
      gridModel.onMount();
      return gridModel.onUnmount;
      // eslint-disable-next-line
    }, []);

    return (
      <>
        <IF condition={isLoading}>
          <PositionedSpinner />
        </IF>
        <IF condition={!isLoading}>
          <IF condition={gridToolbarType === "all" || gridToolbarType === "link-modal"}>
            <ModalGridToolbar
              className="stakeholder-action-grid"
              onQuichSearchInputChange={e => {
                gridRef?.current?.setSearchText(e.currentTarget.value);
              }}
              selectedItems={gridModel.selectedItems}
              preSelectedItemIds={preSelectedItemIds}
              onAssignSuccess={onAssignSuccess}
            />
          </IF>
          <IF condition={gridToolbarType === "all" || gridToolbarType === "base"}>
            <AppGridToolbar
              connectedUsers={connectedUsers}
              onQuichSearchInputChange={e => {
                gridRef.current.setSearchText(e.currentTarget.value);
              }}
              selectedItemCount={gridModel.selectedItems?.length}
              actions={gridModel.gridActions}
            />
          </IF>
          <AppGrid
            ref={gridRef}
            gridOptions={{
              context: { deleteFn: gridModel.deleteFieldData },
              enableBrowserTooltips: true
            }}
            filterStoreAddon={gridModel.filterStoreAddon}
            columnStateAddon={gridModel.columnStateAddon}
            textWrapperAddon={gridModel.textWrapAddon}
            onGridStateUpdate={gridModel.onGridStateUpdate}
            rowData={data}
            columnDefs={gridModel.gridColumns}
            shouldTrackSelectedCell={true}
            connectedUsers={connectedUsers}
            onCellClicked={onCellStateChange && onCellStateChange(organisationId, +projectId)}
            onCellEditModeChange={onCellStateChange && onCellStateChange(organisationId, +projectId)}
            rowSelection="multiple"
          />
        </IF>
      </>
    );
  }
);
